'use client'
import React from 'react'
import { useFormik } from 'formik'
import { forgotPasswordValidationSchema } from '@/src/validations/formvalidations'
import { Button } from '@heroui/react'
import { toast } from '@/src/lib/toast'
import type { AuthView } from './LoginSignupIndex'
import { useUserForgotPasswordMutation } from '@/src/store/rtkQueries/userAuthApi'

interface ForgotPasswordProps {
    navigate: (view: AuthView) => void
    /** Called after the API accepts the request so the OTP step can use the same email. */
    onResetCodeSent?: (email: string) => void
}

export default function ForgotPassword({ navigate, onResetCodeSent }: ForgotPasswordProps) {
    const [userForgotPassword, { isLoading: isSubmitting }] = useUserForgotPasswordMutation()
    const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
        initialValues: {
            email: '',
        },
        validationSchema: forgotPasswordValidationSchema,
        onSubmit: async (values) => {
            try {
                const response = await userForgotPassword({ email: values.email })
                if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                    toast.success(response?.data?.message || 'Check your email for a reset code')
                    onResetCodeSent?.(values.email)
                    navigate('ForgotVerifyOtp')
                }
            } catch (error) {
                console.error('forgot password', error)
            }
        },
    })

    return (
        <>
            <div className="flex flex-col gap-6 w-full">
                {/* Heading */}
                <div className="space-y-2">
                    <h1 className="secondary_heading text-body-color">Forgot <span className="font-secondary italic">Password?</span></h1>
                    <p className="text-sm">By creating an account, I am also consenting to receive SMS, messages and emails.</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Email */}
                    <div className="input_container">
                        <label className="form_label">Email address <span className="required_field">*</span></label>
                        <input
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="example@xyz.com"
                            className="form_input"
                        />
                        {errors.email && touched.email && (
                            <p className="error_text">{errors.email}</p>
                        )}
                    </div>

                    {/* Submit */}
                    <Button
                        type="submit"
                        className="global_btn rounded_full w-full text-sm font-medium tracking-wide"
                        isDisabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending...' : 'Continue'}
                    </Button>
                </form>

            </div>
            {/* Switch to sign in */}
            <p className="text-sm text-center w-full text-body-color">
                Remember it?{' '}
                <span
                    onClick={() => navigate('SignIn')}
                    className="accent_text"
                >
                    Sign in
                </span>
            </p>
        </>
    )
}
